import { useCallback, useMemo } from 'react';
import { useSnackbar } from 'notistack';

import { Request, RequestError, ErrorResponse } from '~globals/types/api';
import { Nullable } from '~globals/types/commons';
import {
  getReportRoutes,
  getReportRouteItems,
  getGiverReportRouteItems,
  getReportItemTasks,
  getGiverReportItemTasks,
} from '~services/admin/report';
import {
  AdminReportDownloadRoutes,
  AdminReportDownloadRouteItems,
  AdminReportDownloadItemTasks,
} from '~services/admin/report/types';
import { downloadFileByRequest } from '~utils/download';

import { useLazyRequest } from './useLazyRequest';
import { useTypePartnerCompany } from './useTypePartnerCompany';

interface UseDownloaReportParams<P> {
  request: Request<P, Blob, ErrorResponse>;
  successMessage: string;
  errorMessage: string;
}

interface UseDownloaReporReturn<P> {
  loading: boolean;
  error: Nullable<RequestError<ErrorResponse>>;
  onDownload: (params: P) => Promise<void>;
}

export const useDownloadReport = <P>({
  request,
  successMessage,
  errorMessage,
}: UseDownloaReportParams<P>): UseDownloaReporReturn<P> => {
  const { enqueueSnackbar } = useSnackbar();

  const [, loading, error, executeRequest] = useLazyRequest({
    request,
    withPostSuccess: (response) => {
      if (response.headers && response.data) {
        downloadFileByRequest(response.headers, response.data);

        enqueueSnackbar(successMessage, { variant: 'success' });
      } else {
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    },
    withPostFailure: () => {
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });

  const onDownload = useCallback(
    async (params: P) => {
      await executeRequest(params);
    },
    [executeRequest],
  );

  return { loading, error, onDownload };
};

export const useDownloadReportRoutes =
  (): UseDownloaReporReturn<AdminReportDownloadRoutes> => {
    const { loading, error, onDownload } = useDownloadReport({
      request: getReportRoutes,
      successMessage: 'Se ha exportado las rutas correctamente',
      errorMessage: 'No se pudo exportar las rutas, intente nuevamente',
    });

    return { loading, error, onDownload };
  };

export const useDownloadReportItems =
  (): UseDownloaReporReturn<AdminReportDownloadRouteItems> => {
    const { isGiver } = useTypePartnerCompany();

    const request = useMemo(
      () => (isGiver ? getGiverReportRouteItems : getReportRouteItems),
      [isGiver],
    );

    const { loading, error, onDownload } = useDownloadReport({
      request,
      successMessage: 'Se ha exportado las visitas correctamente',
      errorMessage: 'No se pudo exportar las visitas, intente nuevamente',
    });

    return { loading, error, onDownload };
  };

export const useDownloadReportItemTasks =
  (): UseDownloaReporReturn<AdminReportDownloadItemTasks> => {
    const { isGiver } = useTypePartnerCompany();

    const request = useMemo(
      () => (isGiver ? getGiverReportItemTasks : getReportItemTasks),
      [isGiver],
    );

    const { loading, error, onDownload } = useDownloadReport({
      request,
      successMessage: 'Se ha exportado los artículos / tareas correctamente',
      errorMessage:
        'No se pudo exportar los artículos / tareas, intente nuevamente',
    });

    return { loading, error, onDownload };
  };
